import React, { useState } from 'react';
import EditableChecklist from './EditableChecklist';
import { FiPlus, FiTrash2, FiX, FiGrid, FiRefreshCw, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { INITIAL_PROTOCOLS } from '../../data/constants';

const ResearchTab = ({ protocols = [], setProtocols }) => {
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [newSection, setNewSection] = useState({
    title: '',
    description: '',
    iconName: 'Grid',
    colorClass: 'bg-espresso'
  });

  const handleAddSection = (e) => {
    e.preventDefault();
    if (!newSection.title.trim()) return;
    
    const section = {
      ...newSection,
      id: `proto-${Date.now()}`,
      tasks: []
    };
    
    setProtocols(prev => [...(prev || []), section]);
    setNewSection({
      title: '',
      description: '',
      iconName: 'Grid',
      colorClass: 'bg-espresso'
    });
    setIsAddingSection(false);
  };

  const executeReset = () => {
    if (window.confirm('Restore all protocols to factory defaults?')) {
      setProtocols(JSON.parse(JSON.stringify(INITIAL_PROTOCOLS)));
    }
  };

  const initiateDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(id);
    // Auto-cancel after 3 seconds if not clicked again
    setTimeout(() => setConfirmDeleteId(prev => prev === id ? null : prev), 3000);
  };

  const confirmDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setProtocols(prev => (prev || []).filter(p => p.id !== id));
    setConfirmDeleteId(null);
  };

  const updateSectionMeta = (id, field, value) => {
    setProtocols(prev => (prev || []).map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const updateSectionTasks = (id, taskUpdater) => {
    setProtocols(prev => (prev || []).map(p => {
      if (p.id === id) {
        const newTasks = typeof taskUpdater === 'function' ? taskUpdater(p.tasks || []) : taskUpdater;
        return { ...p, tasks: newTasks };
      }
      return p;
    }));
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 sm:px-6 animate-fade-in space-y-12">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-cream-dark pb-8">
        <div>
          <h2 className="text-3xl font-serif font-bold text-espresso">Protocol Management</h2>
          <p className="text-espresso-light/60">Define master operational checklists for your workflow.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button 
            type="button" 
            onClick={executeReset}
            className="flex items-center gap-2 px-6 py-3 bg-white border border-cream-dark text-espresso-light rounded-2xl font-bold text-xs hover:bg-cream transition-all shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} />
            Reset Defaults
          </button>
          <button 
            type="button" 
            onClick={() => setIsAddingSection(true)}
            className="flex items-center gap-2 px-6 py-3 bg-terracotta text-white rounded-2xl font-bold text-xs hover:bg-terracotta/90 transition-all shadow-lg"
          >
            <SafeIcon icon={FiPlus} />
            New Category
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-12">
        {protocols.map((proto) => (
          <div key={proto.id} className="relative group">
            {/* Improved Delete Interaction */}
            <div className="absolute -top-4 -right-4 z-[100] flex items-center gap-2">
              {confirmDeleteId === proto.id ? (
                <button 
                  type="button"
                  onClick={(e) => confirmDelete(e, proto.id)}
                  className="px-4 py-2 bg-red-500 text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-2xl animate-pulse flex items-center gap-2 border-2 border-white"
                >
                  <SafeIcon icon={FiTrash2} />
                  Confirm Delete?
                </button>
              ) : (
                <button 
                  type="button" 
                  onClick={(e) => initiateDelete(e, proto.id)}
                  className="p-3 bg-white border-2 border-cream-dark rounded-full text-red-400 hover:text-red-600 hover:border-red-100 shadow-xl md:opacity-0 group-hover:opacity-100 transition-all transform hover:scale-110 active:scale-95 flex items-center justify-center"
                >
                  <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                </button>
              )}
            </div>
            
            <EditableChecklist 
              title={proto.title} 
              description={proto.description}
              tasks={proto.tasks || []}
              setTasks={(updater) => updateSectionTasks(proto.id, updater)}
              iconName={proto.iconName}
              colorClass={proto.colorClass}
              onMetaUpdate={(field, val) => updateSectionMeta(proto.id, field, val)}
            />
          </div>
        ))}

        {protocols.length === 0 && (
          <div className="py-24 text-center bg-white rounded-3xl border-2 border-dashed border-cream-dark">
            <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiGrid} className="w-8 h-8 text-espresso-light/20" />
            </div>
            <p className="text-espresso-light/40 font-bold uppercase tracking-widest text-sm">No Protocol Sections Defined</p>
            <button 
              onClick={() => setIsAddingSection(true)}
              className="mt-4 text-terracotta font-bold text-xs hover:underline uppercase tracking-tighter"
            >
              + Create your first category
            </button>
          </div>
        )}
      </section>

      {/* Add Section Modal */}
      {isAddingSection && (
        <div className="fixed inset-0 bg-espresso/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-espresso">Create Protocol Category</h3>
              <button type="button" onClick={() => setIsAddingSection(false)} className="p-2 hover:bg-cream rounded-full transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddSection} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-1">Category Title</label>
                <input 
                  autoFocus
                  required
                  className="w-full px-4 py-3 bg-cream/20 border border-cream-dark rounded-xl text-sm focus:border-terracotta outline-none text-espresso" 
                  placeholder="e.g. Quality Control"
                  value={newSection.title}
                  onChange={e => setNewSection({...newSection, title: e.target.value})}
                />
              </div>
              <button type="submit" className="w-full py-4 bg-espresso text-white rounded-xl font-bold text-xs uppercase tracking-widest mt-4 hover:bg-espresso-light transition-all">
                Create Category
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchTab;